import { Link } from "react-router-dom";
import { PageHead } from "@/components/PageHead";
import { Reveal } from "@/components/Reveal";
import { AmbientVideo } from "@/components/AmbientVideo";
import { img, IMG, LOREM } from "@/lib/site";

const values = [
  {
    no: "01",
    title: "Provenance",
    img: IMG.woodBoard1,
    body: "We know where every material comes from, and we can tell you its story.",
  },
  {
    no: "02",
    title: "Precision",
    img: IMG.frame,
    body: "Consistent grading, colour-matching and detail, so what you specify is what arrives.",
  },
  {
    no: "03",
    title: "Patience",
    img: IMG.spiral,
    body: "The right material is worth waiting for. We source deliberately, never carelessly.",
  },
  {
    no: "04",
    title: "Permanence",
    img: IMG.beamsShadow,
    body: "We make accessible materials designed to endure decades of everyday life.",
  },
];

const team = [
  { name: "The Showroom", role: "Samples & Specification", img: IMG.chairwork },
  { name: "The Mill", role: "Timber & Veneer Sourcing", img: IMG.sawing },
  { name: "The Warehouse", role: "Supply & Logistics", img: IMG.cutting },
];

export default function About() {
  return (
    <>
      <PageHead
        eyebrow="Our Story"
        titleLines={["Purveyors of", "fine interiors."]}
        intro="We are a specialist supplier of fine interior materials, based in Auckland and serving designers and architects across New Zealand."
      />

      {/* Brand story */}
      <section className="edge pb-24 md:pb-32" data-testid="about-story">
        <div className="overflow-hidden">
          <img
            src={img("/assets/a4.jpg", 2200)}
            alt="House of David interior in fine timber"
            className="aspect-[16/9] w-full object-cover"
          />
        </div>
        <div className="mt-16 grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-3">
            <span className="eyebrow">Chapter 01 — Origins</span>
          </div>
          <div className="space-y-6 md:col-span-8 md:col-start-4">
            <p className="text-2xl font-light leading-snug tracking-editorial md:text-3xl">
              That the materials and space is made from a core matter more than
              anything applied to it.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              We have travelled to mills, quarries and workshops to find timber,
              veneer and stone worthy of the projects our clients imagine. We bring
              the very best of them home to Auckland and make them effortless to
              specify.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              We don't lay floors or build joinery. Instead, we do one thing
              exceptionally well: we source and supply the finest surfaces, and we
              support the designers and builders who put them to work.
            </p>
          </div>
        </div>
      </section>

      {/* Values grid */}
      <section className="hairline" data-testid="about-values">
        <div className="edge py-10">
          <span className="eyebrow">Values / Philosophy</span>
        </div>
        <div className="grid grid-cols-1 hairline sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <Reveal
              key={v.no}
              delay={i * 0.06}
              className={`border-b hairline p-8 sm:[&:nth-child(odd)]:border-r lg:[&:nth-child(odd)]:border-r-0 lg:border-r hairline lg:last:border-r-0`}
            >
              <div className="mb-8 overflow-hidden">
                <img src={img(v.img, 900)} alt={v.title} className="aspect-square w-full object-cover" />
              </div>
              <span className="eyebrow">{v.no}</span>
              <h3 className="mt-3 text-2xl font-light tracking-editorial">{v.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <AmbientVideo
        src="/assets/videos/ambient-2.mp4"
        label="Considered by nature"
        height="h-[80vh] md:h-[92vh]"
        testid="about-ambient-video"
      />
    </>
  );
}
